import { useState, useCallback } from 'react'
import {
  ShieldCheck,
  Copy,
  Download,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  Loader2,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { QRCodeSVG } from 'qrcode.react'
import { useMfa } from '@/features/auth/hooks/use-mfa'

type EnrollmentStep = 'qr' | 'verify' | 'backup' | 'complete'

interface MfaEnrollmentProps {
  onComplete?: () => void
  onCancel?: () => void
}

export function MfaEnrollment({ onComplete, onCancel }: MfaEnrollmentProps) {
  const { enroll, verify, getBackupCodes, loading, error, clearError } = useMfa()

  const [step, setStep] = useState<EnrollmentStep>('qr')
  const [qrUri, setQrUri] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [factorId, setFactorId] = useState<string>('')
  const [verifyCode, setVerifyCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [copied, setCopied] = useState(false)
  const [enrolling, setEnrolling] = useState(false)

  // Start enrollment when component first renders on QR step
  const handleStartEnrollment = useCallback(async () => {
    if (qrUri) return // Already enrolled
    setEnrolling(true)
    clearError()
    try {
      const result = await enroll()
      setQrUri(result.qrUri)
      setSecret(result.secret)
      setFactorId(result.factorId)
    } catch {
      // Error is set by the hook
    } finally {
      setEnrolling(false)
    }
  }, [enroll, qrUri, clearError])

  // Auto-start enrollment on mount
  useState(() => {
    handleStartEnrollment()
  })

  const handleVerify = async () => {
    if (verifyCode.length !== 6) return
    clearError()
    const success = await verify(factorId, verifyCode)
    if (success) {
      // Generate backup codes
      const codes = await getBackupCodes()
      setBackupCodes(codes)
      setStep('backup')
    }
  }

  const handleCopyBackupCodes = async () => {
    try {
      const text = backupCodes.join('\n')
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API may not be available
    }
  }

  const handleDownloadBackupCodes = () => {
    const text = [
      'Zero Platform - MFA Backup Codes',
      '================================',
      '',
      'Store these codes safely. Each code can only be used once.',
      'If you lose your authenticator app, use one of these codes to sign in.',
      '',
      ...backupCodes,
      '',
      `Generated: ${new Date().toISOString()}`,
    ].join('\n')

    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'zero-platform-backup-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  const stepIndicator = (
    <div className="mb-6 flex items-center justify-center gap-2">
      {(['qr', 'verify', 'backup', 'complete'] as const).map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
              step === s
                ? 'bg-zero-600 text-white'
                : ['qr', 'verify', 'backup', 'complete'].indexOf(step) > i
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-400'
            }`}
          >
            {['qr', 'verify', 'backup', 'complete'].indexOf(step) > i ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              i + 1
            )}
          </div>
          {i < 3 && <ChevronRight className="h-4 w-4 text-gray-300" />}
        </div>
      ))}
    </div>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-zero-600" />
          Set Up Two-Factor Authentication
        </CardTitle>
      </CardHeader>
      <CardContent>
        {stepIndicator}

        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Step 1: QR Code */}
        {step === 'qr' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Scan the QR code below with your authenticator app (such as Google Authenticator,
              Authy, or 1Password).
            </p>

            {enrolling ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : qrUri ? (
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <QRCodeSVG value={qrUri} size={192} />
                </div>

                <div className="w-full">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    Or enter this secret manually:
                  </p>
                  <code className="block rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-800 break-all select-all">
                    {secret}
                  </code>
                </div>
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              {onCancel && (
                <Button variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button variant="primary" onClick={() => setStep('verify')} disabled={!qrUri}>
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Verify TOTP */}
        {step === 'verify' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter the 6-digit code shown in your authenticator app to verify the setup.
            </p>

            <Input
              label="Verification Code"
              placeholder="000000"
              value={verifyCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                setVerifyCode(val)
              }}
              maxLength={6}
              className="text-center text-2xl font-mono tracking-[0.5em]"
              autoFocus
            />

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep('qr')}>
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleVerify}
                loading={loading}
                disabled={verifyCode.length !== 6}
              >
                Verify
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Backup Codes */}
        {step === 'backup' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Save your backup codes</p>
                <p className="mt-1">
                  Store these codes safely -- you will need them if you lose access to your
                  authenticator app. Each code can only be used once.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, i) => (
                  <code
                    key={i}
                    className="rounded border border-gray-200 bg-white px-3 py-1.5 text-center text-sm font-mono text-gray-800"
                  >
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCopyBackupCodes} className="flex-1">
                <Copy className="mr-1.5 h-4 w-4" />
                {copied ? 'Copied' : 'Copy Codes'}
              </Button>
              <Button variant="outline" onClick={handleDownloadBackupCodes} className="flex-1">
                <Download className="mr-1.5 h-4 w-4" />
                Download
              </Button>
            </div>

            <div className="flex justify-end">
              <Button variant="primary" onClick={() => setStep('complete')}>
                I have saved my codes
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && (
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Two-Factor Authentication is Active
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Your account is now protected with an additional layer of security. You will be
                asked for a verification code each time you sign in.
              </p>
            </div>

            <Button variant="primary" onClick={onComplete} className="mx-auto">
              Done
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
